// @givmo/compliance — a date the organization gives: typed as "Month day, year" or picked from a
// calendar (frame 18b's "Date filed"; frames 03, 04). The value is an ISO civil date or ''.
//
// ⚠ A DATE IS NEVER GUESSED. Text that does not read as one real day stays as typed and yields no
// value, so a form waiting on it stays unsaveable rather than recording a day nobody gave. The
// span is the backend's (1900-01-01 … 2199-12-31, `check_filing_date`).

import { useEffect, useState } from 'react';
import { Calendar, Field, Icon, IconButton, Popover } from '@givmo/design-system';
import { CalendarDays } from 'lucide-react';
import { displayComplianceDate } from './compliance-decode';
import { longComplianceDate } from './compliance-panel-model';

const MIN_YEAR = 1900;
const MAX_YEAR = 2199;

function isoOf(year: number, month: number, day: number): string | undefined {
  if (year < MIN_YEAR || year > MAX_YEAR || month < 1 || month > 12 || day < 1) return undefined;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return undefined;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const MONTH_NAMES = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

/** "Dec", "Dec.", "Sept", "December" → 12; anything shorter than three letters reads as nothing. */
function monthOf(word: string): number | undefined {
  const name = word.toLowerCase().replace(/\.$/, '');
  if (name.length < 3) return undefined;
  const index = MONTH_NAMES.findIndex((month) => month.startsWith(name));
  return index === -1 ? undefined : index + 1;
}

/** Read typed text as one real day: "Dec 10, 2025", "December 10, 2025", "12/10/2025", "2025-12-10". */
export function parseComplianceDate(text: string): string | undefined {
  const trimmed = text.trim();
  if (trimmed.length === 0) return undefined;
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (iso !== null) return isoOf(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (us !== null) return isoOf(Number(us[3]), Number(us[1]), Number(us[2]));
  // A written month, read part by part: the browser's own reading rolls "Feb 30" over into March.
  // The year must be written out too, so "Dec 10" is not quietly this year.
  const written = /^([A-Za-z]+\.?)\s+(\d{1,2}),?\s+(\d{4})$/.exec(trimmed);
  if (written === null) return undefined;
  const month = monthOf(written[1] ?? '');
  return month === undefined ? undefined : isoOf(Number(written[3]), month, Number(written[2]));
}

function localDateOf(iso: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match === null
    ? undefined
    : new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export interface ComplianceDateFieldProps {
  /** The field's label, shown above it; or, with `labelHidden`, its accessible name only. */
  label: string;
  /** The chosen day, ISO, or '' for none. */
  value: string;
  onChange: (iso: string) => void;
  testId: string;
  /** Where a heading beside the field already says what it is (the send sheet's "Sign by"). */
  labelHidden?: boolean;
  /** "November 30, 2026" rather than "Nov 30, 2026" (frame 36's sign-by date). */
  long?: boolean;
  /** One quiet line under the field. */
  helper?: string;
  /**
   * The latest day the calendar offers, ISO (CCM-R1-SIGN-OUTSIDE-B: a signing date is no later than
   * today). A typed later day is still read; the surface holding the field decides what it means.
   */
  latest?: string;
  /** A day picked from the calendar, beside `onChange`: it is whole at once, never typed. */
  onPick?: (iso: string) => void;
}

export function ComplianceDateField({
  label,
  value,
  onChange,
  testId,
  labelHidden = false,
  long = false,
  helper,
  latest,
  onPick,
}: ComplianceDateFieldProps) {
  const shown = (iso: string) => (long ? longComplianceDate(iso) : displayComplianceDate(iso));
  const [text, setText] = useState(value === '' ? '' : shown(value));
  const [open, setOpen] = useState(false);
  // A value set from outside (a calendar pick, a form reset) shows as the date it is.
  useEffect(() => {
    if (value !== '' && parseComplianceDate(text) !== value) setText(shown(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follows the value, not the typing
  }, [value]);
  return (
    <Field
      {...(labelHidden ? { 'aria-label': label } : { label })}
      {...(helper !== undefined ? { helper } : {})}
      value={text}
      placeholder="Month day, year"
      containerClassName="max-w-[240px]"
      onChange={(event) => {
        setText(event.target.value);
        onChange(parseComplianceDate(event.target.value) ?? '');
      }}
      onBlur={() => {
        if (value !== '') setText(shown(value));
      }}
      testId={testId}
      trailing={
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger
            render={<IconButton size="sm" tone="ghost" aria-label="Choose date" />}
            data-testid={`${testId}-calendar-open`}
          >
            <Icon icon={CalendarDays} size={16} aria-hidden />
          </Popover.Trigger>
          <Popover.Content saas aria-label="Choose date" align="end" testId={`${testId}-calendar`}>
            <Calendar
              mode="single"
              selected={value === '' ? undefined : localDateOf(value)}
              {...(latest !== undefined && localDateOf(latest) !== undefined
                ? { disabled: { after: localDateOf(latest) as Date } }
                : {})}
              onSelect={(date: Date | undefined) => {
                setOpen(false);
                if (date === undefined) return;
                const iso = isoOf(date.getFullYear(), date.getMonth() + 1, date.getDate());
                if (iso === undefined) return;
                setText(shown(iso));
                onChange(iso);
                onPick?.(iso);
              }}
              autoFocus
            />
          </Popover.Content>
        </Popover.Root>
      }
    />
  );
}
